import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, Clock, Calendar, ChevronLeft, ChevronRight,
  User, FileText, Stethoscope, Home, Send, CheckCircle, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConciergeContactPage = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reason: '',
    address: '',
    preferredDate: '',
    preferredTime: '',
    insuranceProvider: '',
    notes: ''
  });

  // Contact information
  const contactInfo = {
    phone: ['(562) 349-0860', '(562) 285-7216'],
    email: 'info@ivory-hhc.com',
    address: '3605 Long Beach Blvd, Suite 531, Long Beach, CA 90807',
    hours: 'Monday - Friday: 9:00 AM - 5:30 PM',
    emergencyLine: '(562) 349-0860'
  };

  // Available time slots
  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'
  ];

  // Services offered
  const services = [
    { icon: Home, title: 'Home Visits', desc: 'Comprehensive medical care in the comfort of your home' },
    { icon: Stethoscope, title: 'Physical Exams', desc: 'Annual wellness exams and preventive care' },
    { icon: FileText, title: 'Chronic Care', desc: 'Management of diabetes, hypertension, and more' },
    { icon: User, title: 'Geriatric Care', desc: 'Specialized care for elderly patients' }
  ];

  // Sample appointments for calendar display
  const sampleAppointments = {
    '2025-02-03': [{ time: '9:00 AM', title: 'Dr. Home Visit', location: 'Long Beach Area' }],
    '2025-02-10': [{ time: '10:00 AM', title: 'Dr. Home Visit', location: 'Signal Hill Area' }],
    '2025-02-17': [{ time: '9:00 AM', title: 'Dr. Home Visit', location: 'Carson Area' }],
    '2025-02-24': [{ time: '2:00 PM', title: 'Dr. Home Visit', location: 'Long Beach Area' }]
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDayOfMonth };
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const { daysInMonth, firstDayOfMonth } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateSelect = (day) => {
    const dateKey = formatDateKey(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(dateKey);
    setFormData({ ...formData, preferredDate: dateKey });
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setFormData({ ...formData, preferredTime: time });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/concierge/consultation-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setFormSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date();
  const isToday = (day) => {
    return today.getDate() === day && 
           today.getMonth() === currentMonth.getMonth() && 
           today.getFullYear() === currentMonth.getFullYear();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/images/ivory-logo.png" alt="Ivory Medical Home" className="h-20 w-20 object-contain ivory-logo-spin" />
              <span className="text-2xl font-bold text-orange-500 italic">IVORY</span>
              <span className="text-gray-500">Medical Home</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-gray-700 hover:text-orange-500">Home</Link>
              <Link to="/login" className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                Login
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Stethoscope className="w-16 h-16 mx-auto mb-4 text-indigo-200" />
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Visiting Doctor Concierge
            </h1>
            <p className="text-xl text-indigo-100 max-w-2xl mx-auto">
              Premium medical care delivered to your doorstep. Schedule a home visit with our experienced physicians today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Cards Section */}
      <section className="py-12 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Call Us Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-orange-500" data-testid="call-us-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Call Us</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Phone support is open Monday through Friday from 9am to 5:30pm.
              </p>
              {contactInfo.phone.map((phone, idx) => (
                <a 
                  key={idx}
                  href={`tel:${phone}`}
                  className="block text-lg font-semibold text-orange-500 hover:text-orange-600 mb-2"
                  data-testid={`phone-number-${idx}`}
                >
                  {phone}
                </a>
              ))}
            </div>

            {/* Email Us Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-indigo-500" data-testid="email-us-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-indigo-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Email Us</h3>
              </div>
              <p className="text-gray-600 mb-4">
                For general inquiries & questions, contact us via email.
              </p>
              <a 
                href={`mailto:${contactInfo.email}`}
                className="text-lg font-semibold text-indigo-500 hover:text-indigo-600"
                data-testid="email-link"
              >
                {contactInfo.email}
              </a>
            </div>

            {/* Visit Us Card */}
            <div className="bg-white rounded-xl shadow-lg p-6 border-t-4 border-teal-500" data-testid="visit-us-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-teal-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Visit Us</h3>
              </div>
              <p className="text-gray-700 mb-4">{contactInfo.address}</p>
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent(contactInfo.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-teal-500 hover:text-teal-600 font-medium"
                data-testid="view-map-link"
              >
                <MapPin className="w-4 h-4" /> View on Map
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">Our Concierge Services</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {services.map((service, idx) => (
              <div 
                key={idx} 
                className="text-center p-6 rounded-xl bg-gray-50 hover:bg-indigo-50 transition-colors"
                data-testid={`service-card-${idx}`}
              >
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <service.icon className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendar & Scheduling Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">Schedule an Appointment</h2>
          <p className="text-center text-gray-600 mb-8">
            Feel free to schedule an appointment with our visiting doctors below.
          </p>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calendar */}
            <div className="bg-white rounded-xl shadow-lg p-6" data-testid="appointment-calendar">
              <div className="flex items-center justify-between mb-6">
                <button 
                  onClick={prevMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="prev-month-btn"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h3 className="text-xl font-bold text-gray-800">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h3>
                <button 
                  onClick={nextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  data-testid="next-month-btn"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before the first of the month */}
                {[...Array(firstDayOfMonth)].map((_, idx) => (
                  <div key={`empty-${idx}`} className="h-12"></div>
                ))}
                
                {/* Days of the month */}
                {[...Array(daysInMonth)].map((_, idx) => {
                  const day = idx + 1;
                  const dateKey = formatDateKey(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                  const hasAppointment = sampleAppointments[dateKey];
                  const isSelected = selectedDate === dateKey;
                  const isPast = new Date(dateKey) < new Date(today.toDateString());

                  return (
                    <button
                      key={day}
                      onClick={() => !isPast && handleDateSelect(day)}
                      disabled={isPast}
                      className={`h-12 rounded-lg text-sm font-medium relative transition-all
                        ${isToday(day) ? 'bg-orange-500 text-white' : ''}
                        ${isSelected && !isToday(day) ? 'bg-indigo-500 text-white' : ''}
                        ${!isSelected && !isToday(day) && !isPast ? 'hover:bg-indigo-100 text-gray-700' : ''}
                        ${isPast ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                      data-testid={`calendar-day-${day}`}
                    >
                      {day}
                      {hasAppointment && (
                        <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Selected Date Info */}
              {selectedDate && (
                <div className="mt-6 p-4 bg-indigo-50 rounded-lg" data-testid="selected-date-info">
                  <h4 className="font-semibold text-indigo-800 mb-3">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Available Times for {selectedDate}
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {timeSlots.map(time => (
                      <button
                        key={time}
                        onClick={() => handleTimeSelect(time)}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors
                          ${selectedTime === time 
                            ? 'bg-indigo-500 text-white' 
                            : 'bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100'
                          }
                        `}
                        data-testid={`time-slot-${time.replace(/\s/g, '-')}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Consultation Request Form */}
            <div className="bg-white rounded-xl shadow-lg p-6" data-testid="consultation-form">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Request Consultation</h3>
              <p className="text-gray-600 text-sm mb-6">
                All consultations are confidential. We will contact you within 24 hours.
              </p>

              {formSubmitted ? (
                <div className="text-center py-12" data-testid="form-success-message">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-gray-800 mb-2">Request Submitted!</h4>
                  <p className="text-gray-600 mb-4">
                    Thank you for your consultation request. Our team will contact you within 24 hours to confirm your appointment.
                  </p>
                  <button
                    onClick={() => {
                      setFormSubmitted(false);
                      setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        reason: '',
                        address: '',
                        preferredDate: '',
                        preferredTime: '',
                        insuranceProvider: '',
                        notes: ''
                      });
                      setSelectedDate(null);
                      setSelectedTime(null);
                    }}
                    className="text-indigo-500 hover:text-indigo-600 font-medium"
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="John"
                        data-testid="input-first-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="Doe"
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="(555) 123-4567"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Home Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="123 Main St, Long Beach, CA 90807"
                      data-testid="input-address"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Date
                      </label>
                      <input
                        type="text"
                        name="preferredDate"
                        value={formData.preferredDate}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        placeholder="Select from calendar"
                        data-testid="input-preferred-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Time
                      </label>
                      <input
                        type="text"
                        name="preferredTime"
                        value={formData.preferredTime}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        placeholder="Select from calendar"
                        data-testid="input-preferred-time"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason for Visit <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="reason"
                      required
                      value={formData.reason}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="select-reason"
                    >
                      <option value="">Select a reason</option>
                      <option value="Annual Physical">Annual Physical Exam</option>
                      <option value="Chronic Care Management">Chronic Care Management</option>
                      <option value="Follow-up Visit">Follow-up Visit</option>
                      <option value="Sick Visit">Sick Visit</option>
                      <option value="Medication Review">Medication Review</option>
                      <option value="Lab Results Review">Lab Results Review</option>
                      <option value="Post-Hospital Follow-up">Post-Hospital Follow-up</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Insurance Provider
                    </label>
                    <input
                      type="text"
                      name="insuranceProvider"
                      value={formData.insuranceProvider}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Medicare, Blue Cross, etc."
                      data-testid="input-insurance"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      name="notes"
                      rows="3"
                      value={formData.notes}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Any additional information about your medical needs..."
                      data-testid="input-notes"
                    ></textarea>
                  </div>

                  <div className="flex items-start gap-2 text-sm text-gray-600 bg-amber-50 p-3 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p>
                      By submitting this form, you consent to being contacted regarding your consultation request. 
                      All information is kept confidential in accordance with HIPAA regulations.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="submit-consultation-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Submit Request
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Office Hours Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4">Office Hours & Emergency Contact</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="w-6 h-6 text-orange-400 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Regular Hours</p>
                      <p className="text-gray-300">{contactInfo.hours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-6 h-6 text-orange-400 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">24/7 Emergency Line</p>
                      <a href={`tel:${contactInfo.emergencyLine}`} className="text-orange-400 hover:text-orange-300">
                        {contactInfo.emergencyLine}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/50 rounded-xl p-6">
                <h4 className="font-semibold mb-3">Service Areas</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>Long Beach, CA</li>
                  <li>Signal Hill, CA</li>
                  <li>Carson, CA</li>
                  <li>Lakewood, CA</li>
                  <li>Compton, CA</li>
                  <li>And surrounding areas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <iframe
              title="Office Location"
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(contactInfo.address)}`}
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-orange-500 mb-4">IVORY</h3>
              <p className="text-gray-400 text-sm">
                Home Health Care and Medical Home<br />
                Visiting Doctor Concierge Services
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/" className="hover:text-orange-400">Home</Link></li>
                <li><Link to="/login" className="hover:text-orange-400">Login</Link></li>
                <li><a href="#contact" className="hover:text-orange-400">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>{contactInfo.address}</li>
                {contactInfo.phone.map((phone, idx) => (
                  <li key={idx}><a href={`tel:${phone}`} className="hover:text-orange-400">{phone}</a></li>
                ))}
                <li><a href={`mailto:${contactInfo.email}`} className="hover:text-orange-400">{contactInfo.email}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-500">
            <p>&copy; 2025 Ivory Home Health Care and Medical Home. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConciergeContactPage;
